/**
 * POI MiniMap Component
 *
 * Collapsible map showing active POI reports with color-coded markers.
 */

import { useState, useEffect } from 'react'
import { MapContainer, TileLayer, Circle, Marker, Popup, useMap } from 'react-leaflet'
import L from 'leaflet'
import { clsx } from 'clsx'
import type { GeoLocation, QuickActionKey, HierarchyRoomType } from '@/lib/types'
import type { POIEntry } from '@/lib/sync'
import { displayAction } from '@/lib/features'
import { formatDistance } from '@/lib/geo'
import { formatDistanceToNow } from 'date-fns'
import { HierarchyMapOverlay, type SectorMapOverlay } from './HierarchyMapOverlay'

// Fix Leaflet default marker icons
import 'leaflet/dist/leaflet.css'

interface POIMiniMapProps {
  center: GeoLocation
  radiusMeters: number
  userLocation: GeoLocation | null
  pois: POIEntry[]
  onClearPOI?: (poiId: string) => void
  canClearPOIs?: boolean
  className?: string
  // Hierarchy overlay support
  hierarchyType?: HierarchyRoomType
  sectors?: SectorMapOverlay[]
  commandBoundary?: { center: GeoLocation; radiusMeters: number }
  showSiblings?: boolean
  onSectorClick?: (sectorRoomId: string) => void
  selectedSectorId?: string
}

export function POIMiniMap({
  center,
  radiusMeters,
  userLocation,
  pois,
  onClearPOI,
  canClearPOIs = false,
  className,
  // Hierarchy props
  hierarchyType,
  sectors = [],
  commandBoundary,
  showSiblings: _showSiblings = false, // Reserved for future sibling sector display
  onSectorClick,
  selectedSectorId,
}: POIMiniMapProps) {
  void _showSiblings // Reserved for future use
  const [isExpanded, setIsExpanded] = useState(false)
  const showHierarchy = hierarchyType === 'command' && sectors.length > 0

  // Get active POIs (not expired, not cleared)
  const activePOIs = pois.filter((poi) => {
    if (poi.clearedAt) return false
    if (poi.expiresAt && poi.expiresAt < Date.now()) return false
    return true
  }).slice(0, 15) // Limit to 15

  // Count POIs by type
  const poiCounts = activePOIs.reduce((acc, poi) => {
    acc[poi.type] = (acc[poi.type] || 0) + 1
    return acc
  }, {} as Record<QuickActionKey, number>)

  return (
    <div className={clsx('relative', className)}>
      {/* Collapse/Expand button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={clsx(
          'w-full flex items-center justify-between px-3 py-2',
          'bg-black/40 border border-white/10 rounded-t-lg transition-colors hover:bg-white/5',
          !isExpanded && 'rounded-b-lg'
        )}
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-widest text-secondary">
            {showHierarchy ? 'Command Map' : 'POI Map'}
          </span>
          {activePOIs.length > 0 && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-secondary/20 text-secondary border border-secondary/30">
              {activePOIs.length} POI
            </span>
          )}
          {showHierarchy && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-500/20 text-purple-400 border border-purple-500/30">
              {sectors.length} SECTORS
            </span>
          )}
        </div>
        <span className="text-white/40 text-xs font-mono">{isExpanded ? '[ COLLAPSE ]' : '[ EXPAND ]'}</span>
      </button>

      {/* Map container */}
      {isExpanded && (
        <div className="border border-t-0 border-white/10 rounded-b-lg overflow-hidden relative">
          <div className={clsx('transition-all relative', isExpanded ? 'h-48 md:h-56' : 'h-0')}>
            <div className="absolute inset-0 pointer-events-none z-[400] border-2 border-white/5 border-dashed"></div>
            <MapContainer
              center={[center.latitude, center.longitude]}
              zoom={16}
              className="w-full h-full bg-[#0a0a0a]"
              zoomControl={false}
              attributionControl={false}
            >
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />

              {/* Geofence circle (only show if not showing hierarchy overlay which has its own command boundary) */}
              {!showHierarchy && (
                <Circle
                  center={[center.latitude, center.longitude]}
                  radius={radiusMeters}
                  pathOptions={{
                    color: '#f20d80',
                    fillColor: '#f20d80',
                    fillOpacity: 0.05,
                    weight: 1,
                    dashArray: '5, 5'
                  }}
                />
              )}

              {/* Hierarchy overlay (command room view) */}
              {showHierarchy && hierarchyType && (
                <HierarchyMapOverlay
                  hierarchyType={hierarchyType}
                  commandBoundary={commandBoundary ?? { center, radiusMeters }}
                  sectors={sectors}
                  showCommandBoundary={true}
                  showSectorCentroids={true}
                  showParticipantCounts={true}
                  selectedSectorId={selectedSectorId}
                  onSectorClick={onSectorClick}
                />
              )}

              {/* User location */}
              {userLocation && (
                <UserLocationMarker location={userLocation} />
              )}

              {/* POI markers */}
              {activePOIs.map((poi) => (
                <POIMarker
                  key={poi.id}
                  poi={poi}
                  userLocation={userLocation}
                  onClear={canClearPOIs ? () => onClearPOI?.(poi.id) : undefined}
                />
              ))}

              {/* Auto-fit bounds */}
              <MapBoundsHandler
                center={center}
                radiusMeters={radiusMeters}
                pois={activePOIs}
              />
            </MapContainer>
          </div>

          {/* Legend */}
          {activePOIs.length > 0 && (
            <div className="px-3 py-2 bg-black/60 border-t border-white/10 flex flex-wrap gap-2 backdrop-blur-sm">
              {Object.entries(poiCounts).map(([type, count]) => {
                const action = displayAction(type as QuickActionKey)
                return (
                  <span
                    key={type}
                    className="text-[10px] font-mono font-bold uppercase px-1.5 py-0.5 rounded border border-white/10"
                    style={{ backgroundColor: `${action.color}10`, color: action.color, borderColor: `${action.color}30` }}
                  >
                    {action.emoji} {count}
                  </span>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * User location marker with accuracy radius
 */
function UserLocationMarker({ location }: { location: GeoLocation }) {
  const userIcon = L.divIcon({
    className: 'user-marker',
    html: `
      <div style="
        width: 14px;
        height: 14px;
        background: #3b82f6;
        border: 2px solid white;
        border-radius: 50%;
        box-shadow: 0 0 10px rgba(59, 130, 246, 0.8);
      "></div>
    `,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  })

  return (
    <>
      {/* Accuracy circle */}
      {location.accuracy && (
        <Circle
          center={[location.latitude, location.longitude]}
          radius={location.accuracy}
          pathOptions={{
            color: '#3b82f6',
            fillColor: '#3b82f6',
            fillOpacity: 0.05,
            weight: 1,
            dashArray: '2, 4'
          }}
        />
      )}
      {/* User position */}
      <Marker
        position={[location.latitude, location.longitude]}
        icon={userIcon}
      />
    </>
  )
}

/**
 * POI marker with popup
 */
function POIMarker({
  poi,
  userLocation,
  onClear,
}: {
  poi: POIEntry
  userLocation: GeoLocation | null
  onClear?: () => void
}) {
  const action = displayAction(poi.type)

  const poiIcon = L.divIcon({
    className: 'poi-marker',
    html: `
      <div style="
        width: 24px;
        height: 24px;
        background: ${action.color};
        border: 2px solid white;
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        box-shadow: 0 0 10px ${action.color};
        transform: rotate(45deg);
      ">
        <div style="transform: rotate(-45deg);">${action.emoji}</div>
      </div>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  })

  // Calculate distance from user
  let distanceText = ''
  if (userLocation) {
    const R = 6371000 // Earth radius in meters
    const dLat = (poi.locationLat - userLocation.latitude) * Math.PI / 180
    const dLon = (poi.locationLng - userLocation.longitude) * Math.PI / 180
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(userLocation.latitude * Math.PI / 180) * Math.cos(poi.locationLat * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    const distance = R * c
    distanceText = formatDistance(distance)
  }

  const timeAgo = formatDistanceToNow(new Date(poi.createdAt), { addSuffix: true })

  return (
    <Marker
      position={[poi.locationLat, poi.locationLng]}
      icon={poiIcon}
    >
      <Popup>
        <div className="text-xs min-w-[140px]">
          <div className="font-bold flex items-center gap-1 uppercase tracking-wider text-black mb-1 p-1" style={{ borderBottom: `2px solid ${action.color}` }}>
            <span>{action.emoji}</span>
            <span>{action.label}</span>
          </div>
          {poi.address && (
            <div className="text-black/70 mt-1 font-mono text-[10px]">📍 {poi.address}</div>
          )}
          <div className="text-black/50 mt-1 text-[10px]">
            {timeAgo}
            {distanceText && ` • ${distanceText} away`}
          </div>
          <div className="text-black/40 text-[10px] mt-1">REP_BY: {poi.reporterName}</div>
          {onClear && (
            <button
              onClick={onClear}
              className="mt-2 w-full px-2 py-1 bg-red-500 text-white text-[10px] uppercase font-bold tracking-wider rounded hover:bg-red-600"
            >
              Clear Marker
            </button>
          )}
        </div>
      </Popup>
    </Marker>
  )
}

/**
 * Auto-adjust map bounds to fit content
 */
function MapBoundsHandler({
  center,
  radiusMeters,
  pois,
}: {
  center: GeoLocation
  radiusMeters: number
  pois: POIEntry[]
}) {
  const map = useMap()

  useEffect(() => {
    // Create bounds that include the geofence circle
    const bounds = L.latLngBounds([
      [center.latitude, center.longitude],
    ])

    // Extend to include geofence radius (approximate)
    const latOffset = radiusMeters / 111000 // ~111km per degree latitude
    const lngOffset = radiusMeters / (111000 * Math.cos(center.latitude * Math.PI / 180))

    bounds.extend([center.latitude + latOffset, center.longitude + lngOffset])
    bounds.extend([center.latitude - latOffset, center.longitude - lngOffset])

    // Include POIs
    pois.forEach((poi) => {
      bounds.extend([poi.locationLat, poi.locationLng])
    })

    map.fitBounds(bounds, { padding: [20, 20] })
  }, [map, center, radiusMeters, pois])

  return null
}
